// Admin → Routing rules: the routing window per customer (`routing-rules.ts`), one row per shown name
// (customers sharing one are one row). A group (two or more codes) has an arrow and its codes under it,
// one per line in the same columns; a single customer is its one row, its Source and Code on it. Groups
// come first. A search that matches a code inside a group opens it. Window in use says the window and
// where it comes from (`rowWindowCell`, the words of the Worklist and Ask AI); Set in app holds only
// what is saved in the app, typed in place or set on many at once from the bar ticking shows. A rule
// saves on every merged code of its row, one record each, one after another (the platform's CRUD has no
// batch). Copy A2000's days saves A2000's days at once; Turn off window saves `noRule` (No routing rule,
// even where A2000 has one), asked first; Use A2000's empties the app's records (Reset). A code may be
// kept separate from its group ("Keep separate from group", on its line): it counts with its own rule
// only, typed on its own line, and is left out of the group's. The list keeps the order the person sees
// until they change a filter or reload (`stable-order.ts`). A code joining its group again (Separate
// off, its Reset, Join group) asks first when the group's window would change, as a rename into a group
// does (`joinImpact`, `rule-check-dialog.tsx`). One typed edit is one save (`edit-gate.ts`). The release
// review (`release-review.tsx`) lists the groups whose window changed when routing rules were released.
// Each save is a change of its own in the System log (restorable there); the server refuses anyone but
// the Admin feature.

import { createContext, useContext, useEffect, useRef, useState } from "react";
import type { MouseEvent } from "react";
import { ChevronDownIcon, ChevronRightIcon, CopyIcon, RotateCcwIcon } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DEFAULT_ROUTING_RULE_FILTERS,
  NO_RULE,
  RULE_DAYS_MAX,
  ROUTING_API,
  BULK_COPY_HOVER,
  RULE_IN_USE_FILTERS,
  aloneLabel,
  bulkCopiedText,
  bulkCopyQuestion,
  bulkCounts,
  copiedText,
  groupSaveText,
  openLinesText,
  separateCounts,
  ruleFilterCounts,
  checkRule,
  codeCopyOffer,
  codeMatchesSearch,
  codeWindowCell,
  copyOffer,
  countText,
  fillOtherSide,
  filterRuleRows,
  formatDay,
  groupSizeText,
  hasRule,
  isRuleGroup,
  joinImpact,
  leftBehind,
  opensOnSearch,
  resetResult,
  rowHasWindow,
  rowWindowCell,
  ruleWords,
  ruleText,
  savedWindowText,
  sharedInUse,
  turnOffPrompt,
  valuesOf,
} from "@app/shared";
import type {
  CustomerNameRecord,
  RenameImpact,
  RoutingRule,
  RoutingRuleFilters,
  RoutingRuleRecord,
  RoutingRuleRow,
  RoutingRulesPayload,
  RuleBase,
  RuleCodeRow,
  RuleKind,
  WindowCell,
} from "@app/shared";
import { Button, Checkbox, DataError, Input, TableSkeleton, apiGet } from "@axny/platform-ui";
import { cn } from "@/lib/utils";
import { SAFETY_REFETCH_MS } from "./data";
import { notify, useMessage } from "./message";
import { useRuleWrites } from "./routing-rule-writes";
import { typedAfterSave } from "./rule-writer";
import type { RuleCheckChoice } from "./rule-check-dialog";
import { useRuleCheck } from "./rule-check-dialog";
import { editGate } from "./edit-gate";
import type { SeenOrder } from "./stable-order";
import { shownOrder } from "./stable-order";
import { ReleaseReview } from "./release-review";
import { SelectionBar, useHeight } from "./selection-bar";
import { STICKY_BELOW_HEADER, STICKY_HEADER_ROW, TABLE_BOX, stickyAbove } from "./sticky";
import { SegmentedViews } from "./segmented";
import { SourceBadge, SourcePicker } from "./source-filter";
import { SearchBox } from "./toolbar";
import { useAdminAskTab } from "./axai-admin";

export const ROUTING_RULES_KEY = ["routing", "admin", "routing-rules"] as const;
/** Ticking every row the filters show asks first above this many. */
const ASK_ABOVE = 200;
const COLUMNS = 11;

/**
 * When the pages count with a saved rule: the server reads the rules on every request (never cached);
 * this browser tab's pages are read again at once (the "routing" queries invalidated), another open page
 * on its focus or its safety refetch (`SAFETY_REFETCH_MS`) — the 15-second check watches A2000 and
 * Deposco only.
 */
const PICKUP = `A Worklist opened now uses it; one already open uses it when you go back to it, or within ${SAFETY_REFETCH_MS / 60_000} minutes.`;

/** A day count in an edit box: "" for none. */
const box = (n: number | null | undefined) => (n === null || n === undefined ? "" : String(n));

/** The tab's one "share one routing window" dialog: asked by any row or the bar (a code joining its group). */
const AskJoin = createContext<(impact: RenameImpact) => Promise<RuleCheckChoice | null>>(async () => ({
  kind: "names",
}));

/** What the buttons' results are counted with: the records, the shown names and A2000's rules. */
interface RuleInputs {
  customerNames: readonly CustomerNameRecord[];
  routingRules: readonly RoutingRuleRecord[];
  base: RuleBase;
}
const Inputs = createContext<RuleInputs | null>(null);
const inputsOf = (p: RoutingRulesPayload): RuleInputs => ({
  customerNames: p.customerNames,
  routingRules: p.routingRules,
  base: p.ruleBase,
});

/** What a save does: days (or No rule), Reset (the records emptied), or Separate from group on / off. */
type RuleOp =
  /** `copied`: Copy A2000's days — its toast says the window no longer follows A2000. */
  | { kind: "rule"; rule: RoutingRule; noRule: boolean; copied?: boolean }
  | { kind: "reset" }
  | { kind: "separate"; on: boolean };

/** The codes a save writes: those named (a code's own line), else every code of the row its op is about. */
const codesFor = (row: RoutingRuleRow, op: RuleOp, only?: ReadonlySet<string>) =>
  row.codes.filter((c) => (only ? only.has(c.key) : op.kind === "separate" || !c.separate));

/**
 * "Turn off window" asked first: the lines it turns off, or the group's app days that still count.
 * `own`: the inputs of a caller above the context (the table, for its bar).
 */
function useTurnOff(own?: RuleInputs | null) {
  const message = useMessage();
  const context = useContext(Inputs);
  const inputs = own ?? context;
  /** `ticked`: the bar's ticked rows, when only some of them have a window (said first). */
  return async (rows: readonly { shown: string; codes: readonly RuleCodeRow[] }[], ticked?: number) => {
    const of =
      ticked && ticked > rows.length
        ? `${rows.length.toLocaleString("en-US")} of ${ticked.toLocaleString("en-US")} selected have a window; `
        : "";
    if (!inputs)
      return message.confirm(
        "Turn off the routing window?",
        `${of ? `${of}their` : "Their"} open lines will show "No routing rule" until you set days or use A2000's again.`,
        { confirmLabel: "Turn off window" },
      );
    const p = turnOffPrompt(
      { ...inputs, customers: rows.flatMap((r) => r.codes) },
      rows.map((r) => ({
        shown: r.shown,
        keys: r.codes.map((c) => c.key),
        openLines: r.codes.reduce((n, c) => n + c.openLines, 0),
      })),
    );
    return message.confirm(p.title, of ? of + p.text.charAt(0).toLowerCase() + p.text.slice(1) : p.text, {
      confirmLabel: p.confirmLabel,
    });
  };
}

/**
 * Saving on rows: the shared run, its report, and the list read again. Returns the rows that failed;
 * null when the person cancelled. Keep separate says first when a group's window changes for the
 * codes left in it (`leftBehind`): a warning, never a block. Saved, the toast says the window it
 * counts with now, read back from the list.
 */
function useRuleRun() {
  const message = useMessage();
  const queryClient = useQueryClient();
  const writes = useRuleWrites();
  const askJoin = useContext(AskJoin);
  const one = (c: RuleCodeRow, op: RuleOp, notes: string, note: string) => {
    const t = { source: c.source, code: c.code, name: c.name, record: c.record };
    const had = valuesOf(c.record);
    if (op.kind === "reset") return writes.reset(t, note);
    if (op.kind === "rule")
      // A merged code's days are the group's; a separate code's (its own line) stay its own.
      return writes.save(t, { ...op.rule, noRule: op.noRule, separate: c.separate }, notes, note);
    // Separate on: its days kept. Off: a record kept only for it is removed (its days, if any, join the group's).
    if (op.on) return writes.save(t, { ...had, separate: true }, String(c.record?.notes ?? ""), note);
    if (!c.record) return Promise.resolve();
    return had.noRule || hasRule(had)
      ? writes.save(t, { ...had, separate: false }, String(c.record.notes ?? ""), note)
      : writes.reset(t, note);
  };
  return async (
    rows: readonly RoutingRuleRow[],
    op: RuleOp,
    notes: string,
    note: string,
    only?: ReadonlySet<string>,
    /** No toast: the caller says the result of several runs at once. */
    quiet = false,
  ): Promise<ReadonlySet<string> | null> => {
    const payload = queryClient.getQueryData<RoutingRulesPayload>(ROUTING_RULES_KEY);
    if (op.kind === "separate" && op.on && payload) {
      const behind = leftBehind({
        customers: payload.rows.flatMap((r) => r.codes),
        customerNames: payload.customerNames,
        routingRules: payload.routingRules,
        base: payload.ruleBase,
        separate: rows.flatMap((r) => codesFor(r, op, only).map((c) => c.key)),
      });
      if (
        behind.length &&
        !(await message.confirm(
          "A group's routing window changes",
          "The codes left in the group count with a different window once these are kept separate.",
          { lines: behind.map((g) => g.text), confirmLabel: "Keep separate" },
        ))
      )
        return null;
    }
    // Codes kept separate joining their group again (Separate off, their Reset): asked first when the
    // group's window would change — names only (join), the group's own window, or keep their own (don't join).
    const joining = rows.flatMap((r) =>
      codesFor(r, op, only)
        .filter((c) => c.separate && (op.kind === "reset" || (op.kind === "separate" && !op.on)))
        .map((c) => c.key),
    );
    let setAfter: { rule: RoutingRule; noRule: boolean; notes: string; members: string[] } | null = null;
    if (joining.length && payload) {
      const impact = joinImpact({
        customers: payload.rows.flatMap((r) => r.codes),
        customerNames: payload.customerNames,
        routingRules: payload.routingRules,
        base: payload.ruleBase,
        keys: joining,
        reset: op.kind === "reset",
      });
      if (impact.dialog !== "none") {
        const choice = await askJoin(impact);
        if (!choice || choice.kind === "own") return null;
        if (choice.kind === "set") setAfter = { ...choice, members: impact.joined?.members.map((m) => m.key) ?? [] };
      }
    }
    const failedRows = new Set<string>();
    let saved = 0;
    const lines: string[] = [];
    try {
      for (const row of rows) {
        const codes = codesFor(row, op, only);
        const r = await writes.each(
          codes.map((c) => ({ source: c.source, code: c.code, name: c.name, record: c.record })),
          (t) =>
            one(
              codes.find((c) => c.source === t.source && c.code === t.code)!,
              op,
              notes,
              note,
            ),
        );
        saved += r.saved;
        if (r.failed.length) failedRows.add(row.shown);
        for (const f of r.failed) lines.push(`${f.target.source} ${f.target.code} · ${f.target.name} — ${f.why}`);
      }
      // "Set the routing window…" on a join: the group's own window on every code of it, once joined.
      const then = setAfter;
      if (then && payload) {
        const fresh = await queryClient.fetchQuery({
          queryKey: ROUTING_RULES_KEY,
          queryFn: ({ signal }) => apiGet<RoutingRulesPayload>(`${ROUTING_API}/admin/routing-rules`, signal),
          staleTime: 0,
        });
        const codes = fresh.rows.flatMap((r) => r.codes).filter((c) => then.members.includes(c.key));
        const r = await writes.each(
          codes.map((c) => ({ source: c.source, code: c.code, name: c.name, record: c.record })),
          (t) =>
            writes.save(
              t,
              { ...then.rule, noRule: then.noRule, separate: false },
              then.notes,
              "Set when joining its group",
            ),
        );
        saved += r.saved;
        for (const f of r.failed) lines.push(`${f.target.source} ${f.target.code} · ${f.target.name} — ${f.why}`);
      }
    } finally {
      await queryClient.invalidateQueries({ queryKey: ["routing"] });
    }
    if (lines.length)
      await message.error(
        `Saved ${saved} · ${lines.length} failed`,
        "The failed ones stay ticked, and a box keeps what you typed: try again, or check them one by one.",
        lines,
      );
    else if (!quiet)
      notify(
        savedText(queryClient.getQueryData<RoutingRulesPayload>(ROUTING_RULES_KEY), rows, saved, op, only),
        PICKUP,
      );
    return failedRows;
  };
}

/**
 * The toast: one customer (or code) and the window it counts with now — a group's row, on how many codes;
 * Copy A2000's days, that it no longer follows A2000; several: how many saved.
 */
function savedText(
  fresh: RoutingRulesPayload | undefined,
  rows: readonly RoutingRuleRow[],
  saved: number,
  op: RuleOp,
  only?: ReadonlySet<string>,
): string {
  const row = rows.length === 1 ? fresh?.rows.find((r) => r.shown === rows[0]!.shown) : undefined;
  if (!row) return `Saved ${countText(saved, "code")} on ${countText(rows.length, "customer")}`;
  const code = only?.size === 1 ? row.codes.find((c) => only.has(c.key)) : undefined;
  const codes = !code && isRuleGroup(row) && op.kind === "rule" ? saved : 1;
  if (op.kind === "rule" && op.copied)
    return copiedText(code ? `${row.shown} · ${code.code}` : row.shown, op.rule, codes);
  return code
    ? savedWindowText(`${row.shown} · ${code.code}`, codeWindowCell(code))
    : savedWindowText(row.shown, rowWindowCell(row), codes);
}

/** A typed rule checked: refused, or confirmed past its warning (Route by alone). Null: not saved. */
function useCheckedRule() {
  const message = useMessage();
  return async (open: string, due: string): Promise<RoutingRule | null> => {
    const check = checkRule(open, due);
    if (!check.ok) {
      await message.error("Check the routing rule", check.message);
      return null;
    }
    if (
      check.warning &&
      !(await message.confirm(check.warning.title, check.warning.text, { confirmLabel: "Save it anyway" }))
    )
      return null;
    return check.rule;
  };
}

export function RoutingRulesTab({ initialInUse }: { initialInUse?: RuleKind }) {
  const check = useRuleCheck();
  return (
    <AskJoin.Provider value={check.ask}>
      {check.dialog}
      <RulesTable initialInUse={initialInUse} />
    </AskJoin.Provider>
  );
}

function RulesTable({ initialInUse }: { initialInUse?: RuleKind }) {
  const message = useMessage();
  const run = useRuleRun();
  const checked = useCheckedRule();
  const [filters, setFilters] = useState<RoutingRuleFilters>({
    ...DEFAULT_ROUTING_RULE_FILTERS,
    ...(initialInUse ? { inUse: initialInUse } : {}),
  });
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set());
  const [anchor, setAnchor] = useState<string | null>(null);
  /** Arrows the person clicked; otherwise a group is open when the search matches a code inside it. */
  const [toggled, setToggled] = useState<ReadonlyMap<string, boolean>>(new Map());
  const [typed, setTyped] = useState({ open: "", due: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const data = useQuery({
    queryKey: ROUTING_RULES_KEY,
    queryFn: ({ signal }) => apiGet<RoutingRulesPayload>(`${ROUTING_API}/admin/routing-rules`, signal),
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
  const turnOff = useTurnOff(data.data ? inputsOf(data.data) : null);
  const rows = data.data?.rows ?? [];
  // The order the person sees stays while they work: a save that moves a row (a rule set) leaves it
  // where it was until a filter changes or the page is loaded again.
  const seen = useRef<SeenOrder<RoutingRuleFilters> | null>(null);
  const shown = shownOrder(filterRuleRows(rows, filters), (r) => r.shown, filters, seen.current);
  useEffect(() => {
    if (data.data) seen.current = { filters, order: shown.map((r) => r.shown) };
  });
  const set = (next: Partial<RoutingRuleFilters>) => {
    // A new search opens the groups it matches inside again, whatever arrows were clicked before.
    if (next.search !== undefined) setToggled(new Map());
    setFilters((f) => ({ ...f, ...next }));
  };
  const applying = useRef(false);
  // The bar's height, measured: the table's header row sticks right under it.
  const [barRef, barHeight] = useHeight<HTMLDivElement>();
  // Ask AI sets the filter buttons and the search (`axai-admin.ts`).
  useAdminAskTab({
    view: { tab: "routing-rules", filters },
    apply: (v) => {
      if (v.tab !== "routing-rules") return;
      setToggled(new Map());
      setFilters(v.filters);
    },
    shownFor: (v) => (v.tab === "routing-rules" ? filterRuleRows(rows, v.filters).length : 0),
  });

  if (data.isPending) return <TableSkeleton />;
  if (data.isError) return <DataError error={data.error} />;

  const tick = (row: RoutingRuleRow, on: boolean, e?: MouseEvent) => {
    const keys =
      e?.shiftKey && anchor
        ? (() => {
            const ids = shown.map((r) => r.shown);
            const from = ids.indexOf(anchor);
            const to = ids.indexOf(row.shown);
            return from < 0 ? [row.shown] : ids.slice(Math.min(from, to), Math.max(from, to) + 1);
          })()
        : [row.shown];
    setTicked((t) => {
      const next = new Set(t);
      for (const k of keys) {
        if (on) next.add(k);
        else next.delete(k);
      }
      return next;
    });
    setAnchor(row.shown);
  };
  const allTicked = shown.length > 0 && shown.every((r) => ticked.has(r.shown));
  const tickAll = async (on: boolean) => {
    if (!on) return setTicked(new Set());
    if (
      shown.length > ASK_ABOVE &&
      !(await message.confirm(
        `Tick ${shown.length.toLocaleString("en-US")} customers?`,
        "Every customer the filters show is ticked. Narrow the filters first to tick fewer.",
        { confirmLabel: `Tick ${shown.length.toLocaleString("en-US")}` },
      ))
    )
      return;
    setTicked(new Set(shown.map((r) => r.shown)));
  };
  const selected = rows.filter((r) => ticked.has(r.shown));
  const counts = bulkCounts(selected);
  const apart = separateCounts(selected);

  /** The bar's actions on the ticked rows (`on`: those it applies to); only the failed ones stay ticked. */
  const bulk = async (op: RuleOp, notes: string, note: string, on: readonly RoutingRuleRow[] = selected) => {
    if (!on.length) return;
    setBusy(true);
    try {
      const failed = await run(on, op, notes, note);
      if (!failed) return;
      setTicked(failed);
      if (!failed.size && op.kind === "rule") setTyped({ open: "", due: "", notes: "" });
    } finally {
      setBusy(false);
    }
  };
  const applyTyped = async () => {
    // Enter and the button, or Enter twice: one Apply at a time.
    if (applying.current) return;
    applying.current = true;
    try {
      await applyChecked();
    } finally {
      applying.current = false;
    }
  };
  const applyChecked = async () => {
    const rule = await checked(typed.open, typed.due);
    if (!rule) return;
    if (!hasRule(rule))
      return void (await message.error(
        "Type Window opens or Route by",
        "Both empty would be no window: use Turn off window for that, or Use A2000's to count with A2000's again.",
      ));
    await bulk(
      { kind: "rule", rule, noRule: false },
      typed.notes.trim(),
      `Set on ${countText(selected.length, "customer")} at once`,
    );
  };
  /** Copy A2000's days on each ticked row it applies to: each row its own days in use (`copyOffer`), one run per days. */
  const copyA2000 = async () => {
    const byDays = new Map<string, { rule: RoutingRule; rows: RoutingRuleRow[] }>();
    for (const r of selected) {
      const rule = copyOffer(r).rule;
      if (!rule) continue;
      const k = ruleText(rule);
      byDays.set(k, { rule, rows: [...(byDays.get(k)?.rows ?? []), r] });
    }
    if (!byDays.size) return;
    // As a row's Copy asks (`checkRule`): A2000's days with one side missing, named.
    const ask = bulkCopyQuestion(selected);
    if (ask && !(await message.confirm(ask.title, ask.text, { lines: ask.lines, confirmLabel: "Copy anyway" }))) return;
    setBusy(true);
    const failed = new Set<string>();
    let cancelled = false;
    try {
      for (const { rule, rows: on } of byDays.values()) {
        const r = await run(
          on,
          { kind: "rule", rule, noRule: false, copied: true },
          typed.notes.trim(),
          "Copied A2000's days",
          undefined,
          true,
        );
        if (!r) cancelled = true;
        else r.forEach((x) => failed.add(x));
      }
      const done = [...byDays.values()].reduce((n, g) => n + g.rows.length, 0) - failed.size;
      if (!cancelled && !failed.size) notify(bulkCopiedText(done), PICKUP);
      setTicked(failed);
    } finally {
      setBusy(false);
    }
  };
  /** Turn off window on the ticked rows that have one; the confirm says how many of the ticked ones. */
  const turnOffSelected = async () => {
    const on = selected.filter(rowHasWindow);
    const targets = on.map((r) => ({ shown: r.shown, codes: codesFor(r, { kind: "reset" }) }));
    if (!(await turnOff(targets, selected.length))) return;
    await bulk({ kind: "rule", rule: NO_RULE, noRule: true }, typed.notes.trim(), "Set no rule", on);
  };
  /** A bar box typed into while both are empty: the other gets the ticked rows' shared day for its side. */
  const typeBar = (side: "open" | "due", v: string) =>
    setTyped((t) => {
      const next = { ...t, [side]: v };
      return t.open.trim() || t.due.trim() ? next : fillOtherSide(next, side, sharedInUse(selected));
    });

  const inUseCounts = ruleFilterCounts(rows, filters);
  return (
    <Inputs.Provider value={data.data ? inputsOf(data.data) : null}>
      <div className="space-y-3">
        {data.data?.review.length ? <ReleaseReview groups={data.data.review} rows={rows} /> : null}
        <div className="flex flex-wrap items-center gap-2">
          <SourcePicker
            value={filters.source}
            counts={{
              A2000: rows.filter((r) => r.codes.some((c) => c.source === "A2000")).length,
              Deposco: rows.filter((r) => r.codes.some((c) => c.source === "Deposco")).length,
            }}
            onChange={(source) => set({ source })}
          />
          <SearchBox
            value={filters.search}
            onChange={(search) => set({ search })}
            placeholder="Search customers — name, code"
          />
          <label className="flex items-center gap-1.5 text-xs">
            <Checkbox checked={filters.withOpenLines} onCheckedChange={(c) => set({ withOpenLines: !!c })} />
            With open lines
          </label>
          <SegmentedViews
            label="Window in use"
            value={filters.inUse}
            onChange={(inUse) => set({ inUse })}
            testId="routing-rules-in-use"
            segments={RULE_IN_USE_FILTERS.map((b) => ({ ...b, n: inUseCounts[b.id], warn: b.id === "none" }))}
          />
          {/* The buttons give the row counts; the open lines of the rows shown are the one thing they don't. */}
          <p className="text-muted-foreground ml-auto text-xs" data-testid="routing-rules-count">
            {openLinesText(shown)}
          </p>
        </div>

        {/* Kept in view, just below the app's header, while rows are ticked (the hint alone doesn't stick): the page scrolls, not the table. */}
        <div
          ref={barRef}
          className={cn(ticked.size > 0 && STICKY_BELOW_HEADER, "bg-background z-20 -my-1.5 py-1.5")}
          data-testid="routing-rules-bar-sticky"
        >
          <SelectionBar
            active={ticked.size > 0}
            hint="Tick customers to set one window on several at once — Shift+click ticks a range"
            data-testid="routing-rules-bar"
          >
            <span className="font-medium">{ticked.size.toLocaleString("en-US")} selected</span>
            <span className="text-muted-foreground">·</span>
            <span>Opens</span>
            <DaysInput
              label="Window opens for the selected customers"
              value={typed.open}
              disabled={busy}
              onChange={(open) => typeBar("open", open)}
              onEnter={() => void applyTyped()}
            />
            <span>Route by</span>
            <DaysInput
              label="Route by for the selected customers"
              value={typed.due}
              disabled={busy}
              onChange={(due) => typeBar("due", due)}
              onEnter={() => void applyTyped()}
            />
            <span className="text-muted-foreground">·</span>
            <Input
              className="h-7 w-48 text-[13px]"
              aria-label="Note for the selected customers"
              placeholder="Why this window (optional)"
              maxLength={500}
              disabled={busy}
              value={typed.notes}
              onChange={(e) => setTyped((t) => ({ ...t, notes: e.target.value }))}
            />
            <Button
              size="sm"
              disabled={busy || (!typed.open.trim() && !typed.due.trim())}
              onClick={() => void applyTyped()}
            >
              Apply
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="gap-1"
              disabled={busy || !counts.copy}
              title={counts.copy ? BULK_COPY_HOVER : counts.why.copy}
              onClick={() => void copyA2000()}
              data-testid="routing-rules-bulk-copy"
            >
              <CopyIcon className="size-3.5" />
              Copy A2000's days ({counts.copy})
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy || !counts.turnOff}
              title={
                counts.turnOff
                  ? "No routing window for the selected customers that have one, even where A2000 has days: their lines show No routing rule"
                  : counts.why.turnOff
              }
              onClick={() => void turnOffSelected()}
              data-testid="routing-rules-bulk-off"
            >
              Turn off window ({counts.turnOff})
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="gap-1"
              disabled={busy || !counts.reset}
              title={
                counts.reset
                  ? "Remove the app's rule of the selected customers that have one: the window comes from A2000 again"
                  : counts.why.reset
              }
              onClick={() =>
                void bulk(
                  { kind: "reset" },
                  "",
                  "Reset to fallback",
                  selected.filter((r) => r.app.records > 0),
                )
              }
              data-testid="routing-rules-bulk-reset"
            >
              <RotateCcwIcon className="size-3.5" />
              Use A2000's ({counts.reset})
            </Button>
            <span className="text-muted-foreground">·</span>
            <Button
              size="sm"
              variant="outline"
              disabled={busy || !apart.keep}
              title={
                apart.keep
                  ? "Every code of the selected groups counts with its own window, out of its group's"
                  : apart.why.keep
              }
              onClick={() =>
                void bulk(
                  { kind: "separate", on: true },
                  "",
                  "Kept separate from its group",
                  selected.filter((r) => isRuleGroup(r) && r.codes.some((c) => !c.separate)),
                )
              }
              data-testid="routing-rules-bulk-separate"
            >
              Keep separate ({apart.keep})
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy || !apart.join}
              title={apart.join ? "Every code kept separate joins its group's window again" : apart.why.join}
              onClick={() =>
                void bulk(
                  { kind: "separate", on: false },
                  "",
                  "Joined its group again",
                  selected.filter((r) => r.codes.some((c) => c.separate)),
                )
              }
              data-testid="routing-rules-bulk-join"
            >
              Join group ({apart.join})
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setTicked(new Set())}>
              Clear selection
            </Button>
            {busy ? <span className="text-muted-foreground text-xs">Saving…</span> : null}
          </SelectionBar>
        </div>

        {/* From 1050 px clipped, not a scroll box (one would keep the header row from sticking to the page); narrower, it scrolls sideways. */}
        <div
          className={cn(TABLE_BOX, "rounded-md border")}
          style={stickyAbove(ticked.size > 0 ? barHeight : 0)}
          data-testid="admin-routing-rules"
        >
          {/* No minimum width: at 1366 px the Note box shrinks first; Source and Code never wrap. */}
          <table className="w-full text-[13px]">
            {/* Sticks under the bar (the app's header plus the bar's measured height; none while nothing is ticked). */}
            <thead
              className={cn(STICKY_HEADER_ROW, "bg-locked text-left text-xs shadow-[0_1px_0_var(--border)]")}
              data-testid="routing-rules-head"
            >
              <tr className="[&>th]:px-2 [&>th]:pt-2 [&>th]:font-medium">
                <th rowSpan={2} className="w-8 pb-2">
                  <Checkbox
                    aria-label="Tick every customer the filters show"
                    checked={allTicked}
                    onCheckedChange={(c) => void tickAll(!!c)}
                  />
                </th>
                <th rowSpan={2} className="min-w-36 pb-2">
                  Customer
                </th>
                <th rowSpan={2} className="w-20 pb-2">
                  Source
                </th>
                <th rowSpan={2} className="w-28 pb-2">
                  Code
                </th>
                <th rowSpan={2} className="w-16 pb-2 text-right">
                  Open lines
                </th>
                <th
                  rowSpan={2}
                  className="w-52 pb-2"
                  title="The window the customer's lines count with, and where it comes from"
                >
                  Window in use
                </th>
                <th
                  colSpan={2}
                  className="text-center"
                  title="Only what is saved in the app; empty: not set in the app"
                >
                  Set in app
                </th>
                <th rowSpan={2} className="pb-2">
                  Note
                </th>
                <th rowSpan={2} className="w-32 pb-2">
                  Last changed
                </th>
                <th rowSpan={2} className="w-44 pb-2" />
              </tr>
              <tr className="[&>th]:px-2 [&>th]:pb-2 [&>th]:font-normal">
                <th className="w-20" title="Business days before First ship date">
                  Opens (days)
                </th>
                <th className="w-20" title="Business days before Cancel date">
                  Route by (days)
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS} className="text-muted-foreground p-6">
                    {rows.length ? "No customer matches the filters." : "No customer in the data yet."}
                  </td>
                </tr>
              ) : null}
              {shown.map((r) => (
                <RuleLine
                  key={r.shown}
                  row={r}
                  ticked={ticked.has(r.shown)}
                  expanded={isRuleGroup(r) && (toggled.get(r.shown) ?? opensOnSearch(r, filters.search))}
                  onTick={tick}
                  onExpand={(on) => setToggled((t) => new Map(t).set(r.shown, on))}
                  search={filters.search}
                />
              ))}
            </tbody>
          </table>
        </div>
        <footer className="text-muted-foreground flex flex-wrap gap-x-4 text-xs">
          <span>
            Window opens = First ship date − the days; Route by = Cancel date − the days; business days, weekends and
            holidays skipped · whole numbers 0–{RULE_DAYS_MAX} · not set in the app: A2000's CUST_REF1 / CUST_REF2 · a
            code kept separate counts with its own only · {PICKUP}
          </span>
          <span className="ml-auto">The app's rule counts inside the app only: nothing is written back to A2000</span>
          {data.data && !data.data.master ? (
            <span className="w-full text-amber-700" data-testid="routing-rules-no-master">
              A2000's days show only for customers with open pick tickets: vw_routing_customers has no CUST_REF1 /
              CUST_REF2 columns yet.
            </span>
          ) : null}
        </footer>
      </div>
    </Inputs.Provider>
  );
}

/** A day count's edit box: whole numbers, 0–60; empty shows "—" (nothing saved in the app). */
function DaysInput({
  label,
  value,
  title,
  disabled,
  onChange,
  onEnter,
  onEscape,
  onFocus,
  onBlur,
}: {
  label: string;
  value: string;
  title?: string;
  disabled?: boolean;
  onChange: (v: string) => void;
  onEnter?: () => void;
  onEscape?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
}) {
  return (
    <Input
      className="h-7 w-16 text-right text-[13px] tabular-nums"
      aria-label={label}
      inputMode="numeric"
      maxLength={3}
      placeholder="—"
      title={title}
      disabled={disabled}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={(e) => {
        // Its default cancelled: the same key would otherwise press a button of the dialog the save opens.
        if (e.key === "Enter") {
          e.preventDefault();
          onEnter?.();
        }
        if (e.key === "Escape") onEscape?.();
      }}
    />
  );
}

/**
 * Window in use: the days (bold; No window in the warning colour), where they come from — an app rule's
 * with "not following A2000 (A2000 has …)". `saving`: a group row's boxes focused, line 2 says which
 * codes a save changes instead, in the warning colour (the cell keeps its two lines: nothing moves).
 */
function WindowInUse({ cell, saving }: { cell: WindowCell; saving?: string }) {
  return (
    <div className="text-xs leading-snug" data-testid="routing-rule-window">
      <span
        className={cn(
          "block text-[13px] font-semibold tabular-nums",
          cell.off && !cell.mixed && "text-amber-700",
          cell.mixed && "font-medium",
        )}
        title={cell.hover}
      >
        {cell.days}
      </span>
      {cell.routeByAlone ? (
        <span className="block text-[11px] text-amber-700">No Window opens: the window never opens</span>
      ) : null}
      {saving ? (
        <span className="block font-medium text-amber-700" data-testid="routing-rule-group-save">
          {saving}
        </span>
      ) : (
        <span className="block">
          {cell.source}
          {cell.follow ? <span className="text-muted-foreground">{cell.follow}</span> : null}
        </span>
      )}
    </div>
  );
}

/** Typed days and notes on a line: saved on Enter or on leaving the line's boxes; Esc puts them back. */
function useTypedRule(saved: { open: string; due: string; notes: string }) {
  const [typed, setTypedState] = useState<{ open: string; due: string; notes: string } | null>(null);
  const gate = useRef(editGate());
  const value = typed ?? saved;
  const changed =
    !!typed &&
    (typed.open.trim() !== saved.open || typed.due.trim() !== saved.due || typed.notes.trim() !== saved.notes);
  /** Typing opens the gate; Esc (null) closes it with nothing saved. */
  const setTyped = (next: { open: string; due: string; notes: string } | null) => {
    if (next) gate.current.edit();
    else gate.current.cancel();
    setTypedState(next);
  };
  return { typed, setTyped, value, changed, gate: gate.current };
}

/** "Keep separate from group": a code's own rule only, saved at once. */
function SeparateTick({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <label
      className="inline-flex items-center gap-1 text-xs whitespace-nowrap"
      title="Its own window only, out of the group's"
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.currentTarget.checked)}
        data-testid="routing-rule-separate"
      />
      Keep separate from group
    </label>
  );
}

/** "Saving…" while a line's save runs: its buttons are off until the answer comes back. */
const Saving = () => <span className="text-muted-foreground text-xs">Saving…</span>;

/**
 * A line's buttons keep their place until the page is loaded again: one offered once stays, disabled
 * when it no longer applies, so the next button never moves under the mouse after a click.
 */
function useKeptSlots<K extends string>(now: Record<K, boolean>): Record<K, boolean> {
  const ever = useRef<Partial<Record<K, boolean>>>({});
  const out = {} as Record<K, boolean>;
  for (const k of Object.keys(now) as K[]) {
    if (now[k]) ever.current[k] = true;
    out[k] = !!ever.current[k];
  }
  return out;
}

/** One shown name: its merged codes' rule typed in place, its codes under it when it is a group. */
function RuleLine({
  row,
  ticked,
  expanded,
  search,
  onTick,
  onExpand,
}: {
  row: RoutingRuleRow;
  ticked: boolean;
  expanded: boolean;
  search: string;
  onTick: (row: RoutingRuleRow, on: boolean, e?: MouseEvent) => void;
  onExpand: (on: boolean) => void;
}) {
  const message = useMessage();
  const run = useRuleRun();
  const checked = useCheckedRule();
  const turnOff = useTurnOff();
  const inputs = useContext(Inputs);
  const [busy, setBusy] = useState(false);
  const running = useRef(false);
  const app = row.app.rule;
  const saved = { open: box(app?.openBdays), due: box(app?.dueBdays), notes: row.app.notes };
  const { typed, setTyped, value, changed, gate } = useTypedRule(saved);
  const merged = row.codes.filter((c) => !c.separate);
  // A group's boxes focused: the line under them says which codes a save there changes.
  const [boxFocused, setBoxFocused] = useState(false);
  /** A box typed into while the other is empty: the other gets the window in use's days for its side. */
  const type = (side: "open" | "due", v: string) => {
    const next = { ...value, [side]: v };
    setTyped(typed ? next : fillOtherSide(next, side, row.inUse.mixed ? null : row.inUse.rule));
  };

  const go = async (op: RuleOp, notes: string, note: string) => {
    // One save at a time: a second click before the answer does nothing.
    if (running.current) return;
    running.current = true;
    setBusy(true);
    try {
      const outcome = await run([row], op, notes, note);
      // Saved: the boxes show the saved days again. Failed or cancelled: what was typed stays, to try again.
      if (typedAfterSave(typed, outcome) === null) setTyped(null);
      else gate.edit();
    } finally {
      running.current = false;
      setBusy(false);
    }
  };

  const commit = async () => {
    // Enter, then the blur that follows: the first saves, the second finds the gate closed.
    if (!typed || !gate.take()) return;
    if (!changed) return setTyped(null);
    if (!typed.open.trim() && !typed.due.trim()) {
      if (!row.app.records) return setTyped(null);
      // Both emptied: back to A2000's, as Use A2000's.
      if (typed.open.trim() !== saved.open || typed.due.trim() !== saved.due)
        return go({ kind: "reset" }, "", "Reset to fallback");
      // The note alone, on boxes already empty: a turned-off window kept; the codes' differing days never guessed.
      if (row.app.mixed || !row.app.noRule) {
        gate.edit();
        return void (await message.error(
          "The codes' saved days differ",
          "Type Opens and Route by to save the note with one window for every code.",
        ));
      }
      return go({ kind: "rule", rule: NO_RULE, noRule: true }, typed.notes.trim(), "");
    }
    const rule = await checked(typed.open, typed.due);
    // Refused or not confirmed: the edit stays, the next Enter or blur tries again.
    if (!rule) return void gate.edit();
    await go(
      { kind: "rule", rule, noRule: false },
      typed.notes.trim(),
      merged.length > 1 ? `Set on ${countText(merged.length, "code")} of ${row.shown}` : "",
    );
  };

  const allSeparate = merged.length === 0;
  const copy = copyOffer(row);
  const hasWindow = !allSeparate && rowHasWindow(row);
  const reset =
    row.app.records && inputs
      ? resetResult(
          { ...inputs, customers: merged },
          merged.filter((c) => c.record).map((c) => c.key),
        )
      : null;
  const slots = useKeptSlots({ copy: !!copy.rule, off: hasWindow, reset: !!reset });
  const copyA2000 = async () => {
    if (!copy.rule) return;
    const rule = await checked(box(copy.rule.openBdays), box(copy.rule.dueBdays));
    if (rule) await go({ kind: "rule", rule, noRule: false, copied: true }, value.notes.trim(), "Copied A2000's days");
  };
  const turnOffRow = async () => {
    if (await turnOff([{ shown: row.shown, codes: merged }]))
      await go({ kind: "rule", rule: NO_RULE, noRule: true }, value.notes.trim(), "Set no rule");
  };

  const group = isRuleGroup(row);
  /** A single customer's one code: its Source and Code on the row itself. */
  const single = group ? null : row.codes[0]!;
  const tip = [
    row.shown,
    ...row.codes.map((c) => `${c.source} ${c.code} · ${c.name}${c.separate ? " · kept separate" : ""}`),
  ].join("\n");
  return (
    <>
      <tr
        className={cn(
          "border-t align-middle [&>td]:px-2 [&>td]:py-1",
          busy && "opacity-60",
          ticked && "bg-primary/5",
          row.needsRule && "bg-amber-50/60",
        )}
        data-testid="routing-rule-row"
        // Leaving the row's boxes saves; moving between them does not.
        onBlur={(e) => {
          if (typed && !e.currentTarget.contains(e.relatedTarget)) void commit();
        }}
      >
        <td>
          <input
            type="checkbox"
            aria-label={`Select ${row.shown}`}
            checked={ticked}
            onClick={(e) => onTick(row, e.currentTarget.checked, e)}
            readOnly
          />
        </td>
        <td>
          {group ? (
            <button
              type="button"
              className="flex items-start gap-1 text-left"
              title={tip}
              onClick={() => onExpand(!expanded)}
              aria-expanded={expanded}
            >
              {expanded ? (
                <ChevronDownIcon className="mt-0.5 size-3.5 shrink-0" />
              ) : (
                <ChevronRightIcon className="mt-0.5 size-3.5 shrink-0" />
              )}
              <span className="min-w-0">
                <span className="line-clamp-2 font-medium break-words">{row.shown}</span>
                <span className="text-muted-foreground block text-[11px]" data-testid="routing-rule-group-size">
                  {groupSizeText(row)}
                </span>
              </span>
            </button>
          ) : (
            <span className="line-clamp-2 font-medium break-words" title={tip}>
              {row.shown}
            </span>
          )}
        </td>
        <td className="whitespace-nowrap">{single ? <SourceBadge source={single.source} /> : null}</td>
        <td className="font-mono text-xs whitespace-nowrap">{single?.code}</td>
        <td className="text-right tabular-nums">{row.openLines.toLocaleString("en-US")}</td>
        <td>
          <WindowInUse cell={rowWindowCell(row)} saving={group && boxFocused ? groupSaveText(row) : undefined} />
        </td>
        <td>
          <DaysInput
            label={`Window opens of ${row.shown}, set in the app`}
            value={value.open}
            title={row.app.mixed ? "The codes' saved days differ: open the group to see each" : undefined}
            disabled={busy || allSeparate}
            onChange={(open) => type("open", open)}
            onEnter={() => void commit()}
            onEscape={() => setTyped(null)}
            onFocus={() => setBoxFocused(true)}
            onBlur={() => setBoxFocused(false)}
          />
        </td>
        <td>
          <DaysInput
            label={`Route by of ${row.shown}, set in the app`}
            value={value.due}
            title={row.app.mixed ? "The codes' saved days differ: open the group to see each" : undefined}
            disabled={busy || allSeparate}
            onChange={(due) => type("due", due)}
            onEnter={() => void commit()}
            onEscape={() => setTyped(null)}
            onFocus={() => setBoxFocused(true)}
            onBlur={() => setBoxFocused(false)}
          />
        </td>
        <td>
          {/* A note goes with a rule set in the app: no box on the others. */}
          {row.app.records ? (
            <Input
              className="h-7 w-full min-w-16 text-[13px]"
              aria-label={`Note of ${row.shown}`}
              maxLength={500}
              disabled={busy || allSeparate}
              value={value.notes}
              placeholder="Why this window (optional)"
              onChange={(e) => setTyped({ ...value, notes: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void commit();
                }
                if (e.key === "Escape") setTyped(null);
              }}
            />
          ) : null}
        </td>
        <td className="text-muted-foreground text-xs [overflow-wrap:anywhere]">
          {row.app.records ? (
            <>
              {row.app.updatedBy || "Saved"}
              {row.app.updatedOn ? ` · ${formatDay(row.app.updatedOn)}` : ""}
            </>
          ) : (
            // The empty Set in app boxes say it already: the words on hover only.
            <span title="Not set in the app">—</span>
          )}
        </td>
        <td>
          <div className="flex flex-wrap items-center gap-1">
            {single?.separate ? (
              // Its group is gone (renamed away) but the flag stays: shown only so it can be taken off.
              <SeparateTick
                checked
                disabled={busy}
                onChange={(on) => void go({ kind: "separate", on }, "", `Joined ${row.shown} again`)}
              />
            ) : null}
            {slots.copy ? (
              <Button
                size="xs"
                variant="outline"
                className="gap-1"
                disabled={busy || !copy.rule}
                title={copy.hover}
                onClick={() => void copyA2000()}
              >
                <CopyIcon className="size-3.5" />
                Copy A2000's days
              </Button>
            ) : null}
            {slots.off ? (
              <Button
                size="xs"
                variant="outline"
                disabled={busy || !hasWindow}
                title={
                  hasWindow
                    ? "No routing window, even where A2000 has days: the lines show No routing rule"
                    : "No window to turn off"
                }
                onClick={() => void turnOffRow()}
              >
                Turn off window
              </Button>
            ) : null}
            {slots.reset ? (
              <Button
                size="xs"
                variant="outline"
                className="gap-1"
                disabled={busy || !reset}
                title={reset?.hover ?? "No rule set in the app"}
                onClick={() => void go({ kind: "reset" }, "", "Reset to fallback")}
              >
                <RotateCcwIcon className="size-3.5" />
                {reset?.label ?? "Use A2000's"}
              </Button>
            ) : null}
            {busy ? <Saving /> : null}
          </div>
        </td>
      </tr>
      {expanded
        ? row.codes.map((c) => <CodeLine key={c.key} row={row} code={c} matched={codeMatchesSearch(c, search)} />)
        : null}
    </>
  );
}

/**
 * One code of a group: what it counts with alone (grey; its own window, black, once kept separate),
 * its record and "Keep separate from group" (saved at once). A separate code's own days are typed
 * here (Enter or leaving its boxes saves), with its own buttons. Its Reset empties its record: back
 * in its group, its days gone. A search matching it marks it.
 */
function CodeLine({ row, code: c, matched }: { row: RoutingRuleRow; code: RuleCodeRow; matched: boolean }) {
  const run = useRuleRun();
  const checked = useCheckedRule();
  const turnOff = useTurnOff();
  const inputs = useContext(Inputs);
  const [busy, setBusy] = useState(false);
  const running = useRef(false);
  const own = valuesOf(c.record);
  const saved = { open: box(own.openBdays), due: box(own.dueBdays), notes: String(c.record?.notes ?? "") };
  const { typed, setTyped, value, changed, gate } = useTypedRule(saved);
  const only = new Set([c.key]);
  const type = (side: "open" | "due", v: string) => {
    const next = { ...value, [side]: v };
    setTyped(typed ? next : fillOtherSide(next, side, c.inUse));
  };

  const go = async (op: RuleOp, notes: string, note: string) => {
    // One save at a time: a second click before the answer does nothing.
    if (running.current) return;
    running.current = true;
    setBusy(true);
    try {
      const outcome = await run([row], op, notes, note, only);
      if (typedAfterSave(typed, outcome) === null) setTyped(null);
      else gate.edit();
    } finally {
      running.current = false;
      setBusy(false);
    }
  };
  const commit = async () => {
    if (!typed || !gate.take()) return;
    if (!changed) return setTyped(null);
    // Its own days emptied: its own A2000 rule (or the borrow) again, still separate; else the notes alone.
    if (!typed.open.trim() && !typed.due.trim())
      return go(
        { kind: "rule", rule: NO_RULE, noRule: hasRule(own) ? false : own.noRule },
        typed.notes.trim(),
        hasRule(own) ? "Own days cleared" : "",
      );
    const rule = await checked(typed.open, typed.due);
    if (!rule) return void gate.edit();
    await go({ kind: "rule", rule, noRule: false }, typed.notes.trim(), "Its own rule, kept separate");
  };

  const copy = codeCopyOffer(c);
  const hasWindow = c.separate && hasRule(c.inUse);
  const reset =
    c.record && (own.noRule || own.separate || hasRule(own)) && inputs
      ? resetResult({ ...inputs, customers: [c] }, [c.key])
      : null;
  const slots = useKeptSlots({ copy: !!copy.rule, off: hasWindow, reset: !!reset });
  const copyA2000 = async () => {
    if (!copy.rule) return;
    const rule = await checked(box(copy.rule.openBdays), box(copy.rule.dueBdays));
    if (rule) await go({ kind: "rule", rule, noRule: false, copied: true }, value.notes.trim(), "Copied A2000's days");
  };
  const turnOffCode = async () => {
    if (await turnOff([{ shown: c.code, codes: [c] }]))
      await go({ kind: "rule", rule: NO_RULE, noRule: true }, value.notes.trim(), "Set no rule");
  };
  const hasOwnRecord = !!c.record && (own.noRule || hasRule(own));

  return (
    <tr
      className={cn("text-xs [&>td]:px-2 [&>td]:py-1", matched ? "bg-sky-50" : "bg-muted/30", busy && "opacity-60")}
      data-testid="routing-rule-code"
      data-matched={matched ? "" : undefined}
      onBlur={(e) => {
        if (typed && !e.currentTarget.contains(e.relatedTarget)) void commit();
      }}
    >
      <td />
      <td className="pl-7" title={`${c.name} · ${c.code} · ${c.source}`}>
        <span className="line-clamp-2 break-words">{c.name || <span className="text-muted-foreground">—</span>}</span>
      </td>
      <td className="whitespace-nowrap">
        <SourceBadge source={c.source} />
      </td>
      <td className={cn("font-mono whitespace-nowrap", matched && "font-semibold")}>{c.code}</td>
      <td className="text-right tabular-nums">{c.openLines.toLocaleString("en-US")}</td>
      <td>
        {c.separate ? (
          <WindowInUse cell={codeWindowCell(c)} />
        ) : (
          // In its group it counts with the group's window (the group's row); what it has alone helps decide Keep separate.
          <span
            className="text-muted-foreground"
            title={`${hasRule(c.alone) ? ruleWords(c.alone) : "No routing window"} — if kept separate from ${row.shown}`}
            data-testid="routing-rule-alone"
          >
            {aloneLabel(c)}
          </span>
        )}
      </td>
      {c.separate ? (
        <>
          <td>
            <DaysInput
              label={`Window opens of ${c.code}, set in the app`}
              value={value.open}
              disabled={busy || own.noRule}
              onChange={(open) => type("open", open)}
              onEnter={() => void commit()}
              onEscape={() => setTyped(null)}
            />
          </td>
          <td>
            <DaysInput
              label={`Route by of ${c.code}, set in the app`}
              value={value.due}
              disabled={busy || own.noRule}
              onChange={(due) => type("due", due)}
              onEnter={() => void commit()}
              onEscape={() => setTyped(null)}
            />
          </td>
        </>
      ) : (
        <td colSpan={2} className="text-center">
          {c.record && own.noRule ? (
            <span>Turned off</span>
          ) : c.record && hasRule(own) ? (
            <span className="tabular-nums">{ruleText(own)}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </td>
      )}
      <td>
        {c.separate && hasOwnRecord ? (
          <Input
            className="h-7 w-full min-w-16 text-[12px]"
            aria-label={`Note of ${c.code}`}
            maxLength={500}
            disabled={busy}
            value={value.notes}
            placeholder="Why this window (optional)"
            onChange={(e) => setTyped({ ...value, notes: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void commit();
              }
              if (e.key === "Escape") setTyped(null);
            }}
          />
        ) : (
          <span className="text-muted-foreground truncate">{c.record?.notes ?? ""}</span>
        )}
      </td>
      <td className="text-muted-foreground [overflow-wrap:anywhere]">
        {hasOwnRecord
          ? `${c.record!.updatedBy}${c.record!.updated_at ? ` · ${formatDay(c.record!.updated_at.slice(0, 10))}` : ""}`
          : "Not set in the app"}
      </td>
      <td>
        <div className="flex flex-wrap items-center gap-1">
          <SeparateTick
            checked={c.separate}
            disabled={busy}
            onChange={(on) =>
              void go(
                { kind: "separate", on },
                "",
                on ? `Kept separate from ${row.shown}` : `Joined ${row.shown} again`,
              )
            }
          />
          {slots.copy ? (
            <Button
              size="xs"
              variant="outline"
              className="gap-1"
              disabled={busy || !copy.rule}
              title={copy.hover}
              onClick={() => void copyA2000()}
            >
              <CopyIcon className="size-3.5" />
              Copy A2000's days
            </Button>
          ) : null}
          {slots.off ? (
            <Button
              size="xs"
              variant="outline"
              disabled={busy || !hasWindow}
              title={hasWindow ? "No routing window for this code, even where A2000 has days" : "No window to turn off"}
              onClick={() => void turnOffCode()}
            >
              Turn off window
            </Button>
          ) : null}
          {slots.reset ? (
            <Button
              size="xs"
              variant="outline"
              className="gap-1"
              disabled={busy || !reset}
              title={reset ? `${reset.hover}${c.separate ? " — and back in its group" : ""}` : "No rule set in the app"}
              onClick={() => void go({ kind: "reset" }, "", "Reset to fallback")}
            >
              <RotateCcwIcon className="size-3.5" />
              {reset?.label ?? "Use A2000's"}
            </Button>
          ) : null}
          {busy ? <Saving /> : null}
        </div>
      </td>
    </tr>
  );
}
